import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { ChevronRight, LayoutGrid, Rows3, X } from 'lucide-react';
import * as React from 'react';
import { Link, type To } from 'react-router-dom';

import { CompactMenu, useCondensed, useViewer } from '@/design';
import { readViewerChoice, writeViewerChoice } from '@/design/viewer-store';

/**
 * A work page's lists as cards (SR-456; Metrics updates' stage tiles, SR-315): each a link with
 * its title, survey-api's count, what to do in a few words, and — where it has one — a small chip;
 * the open one marked (`aria-current`) the way a tab is. They wrap onto more rows as the lists
 * grow; `compact` draws them smaller where a page has many — each title and line cut to the card,
 * said whole in its tooltip.
 *
 * Two variants, the viewer's choice (SR-456): `compact`, the default — one line each, its title,
 * count and first live signal as a badge, the hint and chip in its tooltip — and `detailed`, the
 * cards above with every signal as a labelled count and a bar of the card's share of the page. The
 * choice is one for every page and Team Home's My tasks, kept on this browser per viewer
 * (`veritant.cards.<viewer>`) and switched by the toggle at the end of the cards. A work page's
 * compact bar (`condense.tsx`) draws them compact, without the toggle, the choice kept.
 *
 * Detailed cards in one row are one height — the row's tallest — with their words at the top and
 * the room left empty below (SR-516): a card with more signal lines, a bar or a wrapped title makes
 * its row taller, never its neighbours shorter. The compact variant's cards are one line, one height.
 *
 * A signal with `onToggle` is a toggle button of its own (`aria-pressed` its `active`): every work
 * page narrows its list to the rows of any signal pressed, each said by `SignalChips`. A link
 * cannot hold a button, so such a card is a box holding its link — stretched over the box, so the
 * whole card still opens the list — and the buttons above it. A compact card draws its active
 * signals and the first live one besides.
 */
export interface IStateCard {
  key: string;
  to: To;
  label: string;
  count: React.ReactNode;
  /** What to do with the list, in a few words. */
  hint?: React.ReactNode;
  chip?: React.ReactNode;
  /** A compact card's words whole, on hover or focus. */
  tooltip?: React.ReactNode;
  current: boolean;
  /** Replace the history entry, as a page's own control does. */
  replace?: boolean;
  /** The card's own attributes — where a spec finds it. */
  data?: Record<`data-${string}`, string>;
  /** The count's attributes. */
  countData?: Record<`data-${string}`, string>;
  /** Live counts inside the card's list, the first the one a compact card shows. */
  signals?: readonly IStateSignal[];
  /** The lines a detailed card holds for its signals while their read is in flight, so the card
   *  keeps its height when they arrive. */
  signalsPending?: number;
  /** The card's share of the page's total, 0 to 1 — a detailed card's bar. */
  share?: number;
  /** The card's work done over its whole — a detailed card's bar instead of its share. */
  progress?: { done: number; total: number };
}

export type CardVariant = 'compact' | 'detailed';

/** This page load's choice by key — a write the store refused included — and who redraws on one. */
const chosenVariants = new Map<string, CardVariant>();
const variantListeners = new Set<() => void>();

function subscribeVariant(listener: () => void) {
  variantListeners.add(listener);
  return () => variantListeners.delete(listener);
}

function variantOf(key: string): CardVariant {
  let chosen = chosenVariants.get(key);
  if (!chosen) {
    chosen = readViewerChoice(key).variant === 'detailed' ? 'detailed' : 'compact';
    chosenVariants.set(key, chosen);
  }
  return chosen;
}

/** The viewer's card variant and its setter — every page's cards and Home's tiles follow one. */
export function useCardVariant(): [CardVariant, (variant: CardVariant) => void] {
  const key = `veritant.cards.${useViewer()}`;
  const variant = React.useSyncExternalStore(subscribeVariant, () => variantOf(key));
  const choose = React.useCallback(
    (next: CardVariant) => {
      chosenVariants.set(key, next);
      writeViewerChoice(key, { variant: next });
      variantListeners.forEach((listener) => listener());
    },
    [key]
  );
  return [variant, choose];
}

const VARIANTS: ReadonlyArray<{ value: CardVariant; label: string; icon: typeof Rows3 }> = [
  { value: 'compact', label: 'Compact', icon: Rows3 },
  { value: 'detailed', label: 'Detailed', icon: LayoutGrid }
];

/** The two variants as a small segmented control, the chosen one pressed. */
export function CardVariantToggle({ className }: { className?: string }) {
  const [variant, choose] = useCardVariant();
  return (
    <div
      role="group"
      aria-label="Card size"
      data-card-variant={variant}
      className={cn('inline-flex h-7 shrink-0 rounded-md border border-border p-0.5', className)}
    >
      {VARIANTS.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          aria-label={label}
          aria-pressed={variant === value}
          onClick={() => choose(value)}
          className={cn(
            'inline-flex w-7 items-center justify-center rounded-[0.3rem] outline-none focus-visible:ring-2',
            'focus-visible:ring-ring',
            variant === value
              ? 'bg-[var(--color-slight-blue)] text-[var(--color-royal-blue)]'
              : 'text-muted-foreground hover:text-[var(--color-royal-blue)]'
          )}
        >
          <Icon aria-hidden className="size-3.5" />
        </button>
      ))}
    </div>
  );
}

export type SignalTone = 'danger' | 'attention' | 'info' | 'success';

/** A live count inside a card's list — "2 overdue" — read beside its count. */
export interface IStateSignal {
  key: string;
  label: string;
  count: number;
  tone: SignalTone;
  /** Whether the list is narrowed by it — the toggle's `aria-pressed`. */
  active?: boolean;
  /** The signal as a toggle: pressed, the list narrowed by it too, or no longer. */
  onToggle?: () => void;
  /** The toggle's accessible name — "Show 2 overdue DD calls". */
  toggleName?: string;
  /** A badge's words where `label` is too long for a compact card — "new filings" — and what it
   *  means, said in the badge's tooltip; the detailed card and the chips keep `label`. */
  short?: { label: string; tip: string };
}

const SIGNAL_TONE: Record<SignalTone, string> = {
  danger: 'bg-[var(--color-deep-magenta)] text-white',
  attention: 'bg-[color-mix(in_srgb,var(--color-magenta)_12%,white)] text-[var(--color-deep-magenta)]',
  info: 'bg-[var(--veritant-royal-15)] text-[var(--color-royal-blue)]',
  success: 'bg-[color-mix(in_srgb,var(--color-teal)_14%,white)] text-[var(--color-teal)]'
};

/** A signal's words and tone; its count is read under `key`. */
export type SignalSpec = Omit<IStateSignal, 'count'>;

/** The signals whose counts were read, under `prefix` + their key — one left unread is left out. */
export function signalsOf(
  counts: Readonly<Record<string, number>>,
  prefix: string,
  specs: readonly SignalSpec[]
): IStateSignal[] {
  return specs.flatMap((spec) => {
    const count = counts[`${prefix}${spec.key}`];
    return typeof count === 'number' ? [{ key: spec.key, label: spec.label, tone: spec.tone, count }] : [];
  });
}

/** A signal as a small coloured badge: its count and words — a toggle where it has `onToggle`. */
export function SignalBadge({ signal }: { signal: IStateSignal }) {
  const className = cn(
    'inline-flex h-4.5 shrink-0 items-center whitespace-nowrap rounded-full px-1.5',
    'text-[0.6875rem] font-medium tabular-nums',
    signal.count > 0 ? SIGNAL_TONE[signal.tone] : 'bg-muted text-muted-foreground'
  );
  const words = `${signal.count.toLocaleString('en-US')} ${signal.short?.label ?? signal.label}`;
  const badge = signal.onToggle ? (
    <SignalButton signal={signal} className={cn(className, 'hover:underline')}>
      {words}
    </SignalButton>
  ) : (
    <span data-signal={signal.key} className={className}>
      {words}
    </span>
  );
  if (!signal.short) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">{badge}</span>
      </TooltipTrigger>
      <TooltipContent>{signal.short.tip}</TooltipContent>
    </Tooltip>
  );
}

/** One signal the list is narrowed by, in `SignalChips`. */
export interface ISignalChip {
  /** The signal's key — `data-flag-chip`, where a spec finds it. */
  key: string;
  words: string;
  /** The ✕'s accessible name — "Show every task, not only overdue". */
  clearName: string;
  onClear: () => void;
}

/** The signals a list is narrowed by, as one chip of dismissible parts — "Showing: overdue ✕ or
 *  unassigned ✕", the rows of any of them — in the page's chips line; nothing while none is on. */
export function SignalChips({ chips }: { chips: readonly ISignalChip[] }) {
  if (chips.length === 0) return null;
  return (
    <span
      role="group"
      aria-label="Signals shown"
      data-signal-chips=""
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border',
        'border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] pl-3 pr-1 text-sm'
      )}
    >
      <span className="text-muted-foreground">Showing:</span>
      {chips.map((chip, index) => (
        <React.Fragment key={chip.key}>
          {index > 0 && <span className="text-muted-foreground"> or </span>}
          <span data-flag-chip={chip.key} className="inline-flex items-center">
            {chip.words}
            <button
              type="button"
              aria-label={chip.clearName}
              onClick={chip.onClear}
              className={cn(
                'inline-flex size-6 items-center justify-center rounded-full text-muted-foreground outline-none',
                'hover:bg-[var(--veritant-royal-15)] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <X aria-hidden className="size-3.5" />
            </button>
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

/** A signal's toggle, above the card's stretched link; an active one ringed. */
function SignalButton({
  signal,
  className,
  children
}: {
  signal: IStateSignal;
  className?: string;
  children: React.ReactNode;
}) {
  const active = signal.active === true;
  return (
    <button
      type="button"
      data-signal={signal.key}
      aria-label={signal.toggleName}
      aria-pressed={active}
      onClick={signal.onToggle}
      className={cn(
        'relative z-[1] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active && 'ring-2 ring-[var(--color-royal-blue)] ring-offset-1',
        className
      )}
    >
      {children}
    </button>
  );
}

/** Whether a card draws a signal as a button, and so is a box around its link. */
function pressable(card: IStateCard, signals: readonly IStateSignal[] | undefined = card.signals): boolean {
  return signals?.some((signal) => signal.onToggle) ?? false;
}

/** A compact card's badges: its active signals, then the first live one besides. */
function slimSignals(card: IStateCard): IStateSignal[] {
  const signals = card.signals ?? [];
  const live = signals.find((one) => !one.active && one.count > 0);
  return signals.filter((one) => one.active || one === live);
}

/** The link of a card that is a box: stretched over the box, its focus ring the box's. */
const STRETCHED = cn(
  "outline-none after:absolute after:inset-0 after:rounded-md after:content-['']",
  'focus-visible:after:ring-2 focus-visible:after:ring-ring'
);

export function StateCards({
  label,
  cards,
  compact = false,
  end
}: {
  /** The navigation's accessible name. */
  label: string;
  cards: readonly IStateCard[];
  compact?: boolean;
  /** The list's own control drawn at the row's end, before the card size — My tasks' *Opens in*,
   *  a page with no toolbar of its own. */
  end?: React.ReactNode;
}) {
  const [chosen] = useCardVariant();
  const { condensed } = useCondensed();
  const variant = condensed ? 'compact' : chosen;
  return (
    <div className="flex items-start gap-2">
      {variant === 'compact' ? (
        <SlimCards label={label} cards={cards} />
      ) : (
        <DetailedCards label={label} cards={cards} compact={compact} />
      )}
      {!condensed && end && <span className="ml-auto shrink-0">{end}</span>}
      {!condensed && <CardVariantToggle className="mt-1" />}
    </div>
  );
}

function DetailedCards({
  label,
  cards,
  compact
}: {
  label: string;
  cards: readonly IStateCard[];
  compact: boolean;
}) {
  return (
    <nav aria-label={label} className="min-w-0 flex-1">
      <ul
        className={cn(
          'grid gap-2',
          compact
            ? 'grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))]'
            : 'grid-cols-[repeat(auto-fill,minmax(12rem,1fr))]'
        )}
      >
        {cards.map((card) => {
          // A card whose signals are still read draws as it will once they are: no jump.
          if (pressable(card) || card.signalsPending) {
            return <BoxedDetailedCard key={card.key} card={card} compact={compact} />;
          }
          const link = (
            <Link
              to={card.to}
              replace={card.replace}
              aria-current={card.current ? 'page' : undefined}
              {...card.data}
              className={cn(
                'group flex h-full items-start gap-2 rounded-md border transition-colors',
                compact ? 'px-2.5 py-1.5' : 'px-3 py-2',
                card.current
                  ? 'border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] shadow-[inset_0_-3px_0_var(--color-royal-blue)]'
                  : 'border-border hover:border-[var(--color-royal-blue)] hover:bg-[var(--color-slight-blue)]'
              )}
            >
              {compact ? (
                <span className="grid min-w-0 flex-1">
                  <span
                    className={cn(
                      'truncate text-[0.8125rem]',
                      card.current ? 'font-semibold' : 'text-muted-foreground'
                    )}
                  >
                    {card.label}
                  </span>
                  <span className="text-lg font-semibold tabular-nums" {...card.countData}>
                    {card.count}
                  </span>
                  <span className="flex min-w-0 items-center gap-1.5 text-[0.7rem] text-muted-foreground">
                    {card.chip && <span className="min-w-0 max-w-[60%] shrink-0 truncate">{card.chip}</span>}
                    {card.hint && <span className="min-w-0 truncate">{card.hint}</span>}
                  </span>
                  <DetailedSignals card={card} />
                </span>
              ) : (
                <span className="grid flex-1">
                  <span className={cn('text-[0.8125rem]', card.current ? 'font-semibold' : 'text-muted-foreground')}>
                    {card.label}
                  </span>
                  <span className="text-xl font-semibold tabular-nums" {...card.countData}>
                    {card.count}
                  </span>
                  {card.hint && <span className="text-[0.7rem] text-muted-foreground">{card.hint}</span>}
                  <DetailedSignals card={card} />
                </span>
              )}
              <ChevronRight
                aria-hidden
                className={cn(
                  'size-4 shrink-0 self-center',
                  card.current
                    ? 'text-[var(--color-royal-blue)]'
                    : 'text-muted-foreground group-hover:text-[var(--color-royal-blue)]'
                )}
              />
            </Link>
          );
          return (
            <li key={card.key} className="h-full">
              {card.tooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent className="max-w-xs">{card.tooltip}</TooltipContent>
                </Tooltip>
              ) : (
                link
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** A detailed card with a pressable signal: a box, its link stretched over it, the signals' buttons
 *  above the link, the chevron drawn in the box. */
function BoxedDetailedCard({ card, compact }: { card: IStateCard; compact: boolean }) {
  return (
    <li
      className={cn(
        'group relative flex h-full items-start gap-2 rounded-md border transition-colors',
        compact ? 'px-2.5 py-1.5' : 'px-3 py-2',
        card.current
          ? 'border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] shadow-[inset_0_-3px_0_var(--color-royal-blue)]'
          : 'border-border hover:border-[var(--color-royal-blue)] hover:bg-[var(--color-slight-blue)]'
      )}
    >
      <span className="grid min-w-0 flex-1">
        <Link
          to={card.to}
          replace={card.replace}
          aria-current={card.current ? 'page' : undefined}
          {...card.data}
          className={cn('grid min-w-0', STRETCHED)}
        >
          <span
            className={cn(
              compact ? 'truncate text-[0.8125rem]' : 'text-[0.8125rem]',
              card.current ? 'font-semibold' : 'text-muted-foreground'
            )}
          >
            {card.label}
          </span>
          <span
            className={cn(compact ? 'text-lg' : 'text-xl', 'font-semibold tabular-nums')}
            {...card.countData}
          >
            {card.count}
          </span>
          {card.hint && <span className="truncate text-[0.7rem] text-muted-foreground">{card.hint}</span>}
        </Link>
        <DetailedSignals card={card} />
      </span>
      <ChevronRight
        aria-hidden
        className={cn(
          'size-4 shrink-0 self-center',
          card.current
            ? 'text-[var(--color-royal-blue)]'
            : 'text-muted-foreground group-hover:text-[var(--color-royal-blue)]'
        )}
      />
    </li>
  );
}

/** Work done over its whole, 0 to 1, to the percent — 0 for no work at all. */
function shareOf({ done, total }: { done: number; total: number }): number {
  return total > 0 ? Math.round((done / total) * 100) / 100 : 0;
}

const SIGNAL_TEXT: Record<SignalTone, string> = {
  danger: 'text-[var(--color-deep-magenta)]',
  attention: 'text-[var(--color-deep-magenta)]',
  info: 'text-[var(--color-royal-blue)]',
  success: 'text-[var(--color-teal)]'
};

/** A detailed card's signals as small labelled counts, and its share of the page as a thin bar. */
function DetailedSignals({ card }: { card: IStateCard }) {
  const signals = card.signals ?? [];
  const pending = signals.length === 0 ? (card.signalsPending ?? 0) : 0;
  return (
    <>
      {pending > 0 && (
        <span className="mt-1 grid gap-y-0.5" data-signals-pending={pending} aria-hidden>
          {Array.from({ length: pending }, (_, at) => (
            <span key={at} className="h-4 w-24 max-w-full animate-pulse rounded bg-[var(--veritant-royal-15)]" />
          ))}
        </span>
      )}
      {signals.length > 0 && (
        <span className="mt-1 flex min-w-0 flex-wrap gap-x-2.5 gap-y-0.5 text-[0.6875rem] text-muted-foreground">
          {signals.map((signal) => {
            const words = (
              <>
                <span
                  className={cn('font-semibold tabular-nums', signal.count > 0 ? SIGNAL_TEXT[signal.tone] : undefined)}
                >
                  {signal.count.toLocaleString('en-US')}
                </span>{' '}
                {signal.label}
              </>
            );
            return signal.onToggle ? (
              <SignalButton
                key={signal.key}
                signal={signal}
                className={cn(
                  '-mx-1 whitespace-nowrap rounded px-1',
                  'hover:bg-[var(--veritant-royal-15)] hover:text-foreground'
                )}
              >
                {words}
              </SignalButton>
            ) : (
              <span key={signal.key} data-signal={signal.key} className="whitespace-nowrap">
                {words}
              </span>
            );
          })}
        </span>
      )}
      {card.progress ? (
        <span className="mt-1.5 grid gap-0.5" data-progress={`${card.progress.done}/${card.progress.total}`}>
          <span className="text-[0.6875rem] text-muted-foreground tabular-nums">
            done {card.progress.done.toLocaleString('en-US')} of {card.progress.total.toLocaleString('en-US')}
          </span>
          <span className="h-1 overflow-hidden rounded-full bg-[var(--veritant-royal-15)]" aria-hidden>
            <span
              className="block h-full bg-[var(--color-teal)]"
              style={{ width: `${shareOf(card.progress) * 100}%` }}
            />
          </span>
        </span>
      ) : card.share !== undefined && (
        <span className="mt-1.5 h-1 overflow-hidden rounded-full bg-[var(--veritant-royal-15)]" aria-hidden>
          <span
            className="block h-full bg-[var(--color-royal-blue)]"
            style={{ width: `${Math.round(Math.min(1, Math.max(0, card.share)) * 100)}%` }}
          />
        </span>
      )}
    </>
  );
}

/** The compact variant: one line per card — its title, count, active signals and first live one
 *  besides — the hint and the chip said in its tooltip.
 *
 *  In a compact bar, one line: a card's words give way first, its count never; the open card's
 *  signal alone is drawn. Where the cards cannot fit the room the bar gives them whole, the open
 *  one is drawn and the rest are a menu of the cards' name ("Reasons ▾") — measured on a hidden
 *  copy of the whole line, so what is drawn never changes what fits. */
function SlimCards({ label, cards }: { label: string; cards: readonly IStateCard[] }) {
  const { condensed } = useCondensed();
  const nav = React.useRef<HTMLElement>(null);
  const whole = React.useRef<HTMLUListElement>(null);
  const fits = useFits(nav, whole, condensed);
  const open = cards.find((card) => card.current);
  const line = (drawn: readonly IStateCard[]) =>
    drawn.map((card) => {
      const shown = !condensed || card.current ? slimSignals(card) : [];
      const boxed = pressable(card, shown);
      const link = boxed ? (
        <Link
          to={card.to}
          replace={card.replace}
          aria-current={card.current ? 'page' : undefined}
          {...card.data}
          className={cn('flex min-w-0 items-center', condensed ? 'gap-1.5' : 'gap-2', STRETCHED)}
        >
          <span
            className={cn(
              'min-w-0 truncate text-[0.8125rem]',
              card.current ? 'font-semibold' : 'text-muted-foreground'
            )}
          >
            {card.label}
          </span>
          <span className="text-[0.9375rem] font-semibold tabular-nums" {...card.countData}>
            {card.count}
          </span>
        </Link>
      ) : (
        <Link
          to={card.to}
          replace={card.replace}
          aria-current={card.current ? 'page' : undefined}
          {...card.data}
          className={cn(
            'group flex h-9 max-w-[20rem] items-center rounded-md border transition-colors',
            condensed ? 'gap-1.5 px-2' : 'gap-2 px-2.5',
            card.current
              ? 'border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] shadow-[inset_0_-3px_0_var(--color-royal-blue)]'
              : 'border-border hover:border-[var(--color-royal-blue)] hover:bg-[var(--color-slight-blue)]'
          )}
        >
          <span
            className={cn(
              'min-w-0 truncate text-[0.8125rem]',
              card.current ? 'font-semibold' : 'text-muted-foreground'
            )}
          >
            {card.label}
          </span>
          <span className="text-[0.9375rem] font-semibold tabular-nums" {...card.countData}>
            {card.count}
          </span>
          {shown.map((signal) => (
            <SignalBadge key={signal.key} signal={signal} />
          ))}
        </Link>
      );
      const said = card.tooltip ?? (card.hint ? <>{card.label} — {card.hint}</> : null);
      const tipped = said ? (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent className="max-w-xs">{said}</TooltipContent>
        </Tooltip>
      ) : (
        link
      );
      return boxed ? (
        <li
          key={card.key}
          className={cn(
            'relative flex h-9 min-w-0 max-w-[20rem] items-center rounded-md border transition-colors',
            condensed ? 'gap-1.5 px-2' : 'gap-2 px-2.5',
            card.current
              ? 'border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] shadow-[inset_0_-3px_0_var(--color-royal-blue)]'
              : 'border-border hover:border-[var(--color-royal-blue)] hover:bg-[var(--color-slight-blue)]'
          )}
        >
          {tipped}
          {shown.map((signal) => (
            <SignalBadge key={signal.key} signal={signal} />
          ))}
        </li>
      ) : (
        <li key={card.key} className="min-w-0">
          {tipped}
        </li>
      );
    });
  if (!condensed) {
    return (
      <nav aria-label={label} className="min-w-0 flex-1">
        <ul className="flex flex-wrap gap-2">{line(cards)}</ul>
      </nav>
    );
  }
  return (
    <nav ref={nav} aria-label={label} className="relative min-w-0 flex-1">
      <ul ref={whole} aria-hidden className="pointer-events-none invisible absolute top-0 left-0 flex w-max flex-nowrap gap-1.5">
        {line(cards)}
      </ul>
      {fits || !open ? (
        <ul className="flex flex-nowrap gap-1.5">{line(cards)}</ul>
      ) : (
        <ul className="flex flex-nowrap items-center gap-1">
          {line([open])}
          <li className="shrink-0">
            <CompactMenu
              label={label}
              trigger={label}
              items={cards.map((card) => ({
                key: card.key,
                label: card.label,
                to: card.to,
                replace: card.replace,
                current: card.current,
                count: card.count
              }))}
            />
          </li>
        </ul>
      )}
    </nav>
  );
}

/** Whether `whole` — the line of every card, hidden — fits `box`'s width, watched as either
 *  changes; only while `enabled`. */
function useFits(
  box: React.RefObject<HTMLElement>,
  whole: React.RefObject<HTMLElement>,
  enabled: boolean
): boolean {
  const [fits, setFits] = React.useState(true);
  React.useLayoutEffect(() => {
    const outer = box.current;
    const inner = whole.current;
    if (!enabled || !outer || !inner) return;
    const measure = () => setFits(inner.scrollWidth <= outer.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [box, whole, enabled]);
  return fits;
}
